import {
  canProxy,
  getMeta,
  isObject,
  isReactiveState,
  META,
  MicrotaskBatcher,
  type AnyReactiveState,
  type ProxiedReactiveState,
  type ProxyMeta,
  type ReactiveState,
} from './reactive-store.helper';

function getSetterInChain(target: object, prop: string | symbol): PropertyDescriptor | undefined {
  let current: object | null = target;
  while (current) {
    const desc = Object.getOwnPropertyDescriptor(current, prop);
    if (desc?.set) return desc;
    current = Object.getPrototypeOf(current);
  }
  return undefined;
}

const batcher = new MicrotaskBatcher();

/**
 * Erstellt einen "deep" reactive proxy um den state "observable" zu machen.
 * KEINE svelte dependency
 *
 * @param initial - The initial object to create a reactive state from
 *
 */
export function createReactiveState<T extends object>(initial: T): ProxiedReactiveState<T> {
  if (!isObject(initial)) {
    throw new Error('createReactiveStore requires an object');
  }

  const proxyCache = new WeakMap<object, AnyReactiveState>();

  function scheduleNotify(proxy: AnyReactiveState) {
    batcher.scheduleCallbacks(proxy[META]?.watchers);
  }

  const proxyFor = (v: unknown): AnyReactiveState | undefined =>
    isReactiveState(v) ? v
    : isObject(v) ? proxyCache.get(v)
    : undefined;

  function deepProxy<T extends object>(obj: T): ReactiveState<T> {
    if (isReactiveState(obj)) return obj as ReactiveState<T>;
    const cached = proxyCache.get(obj) as ReactiveState<T> | undefined;
    if (cached) return cached;

    const proxyMeta: ProxyMeta = { watchers: new Set() };
    const bubbleCb = () => scheduleNotify(proxy);

    const proxy = new Proxy<T>(obj, {
      get(target, prop) {
        if (prop === META) return proxyMeta;

        const value = Reflect.get(target, prop, proxy);

        if (canProxy(value)) {
          const child = deepProxy(value);
          (child[META] as ProxyMeta).watchers.add(bubbleCb); // idempotent
          return child;
        }

        return value;
      },

      set(target, prop, value, receiver) {
        const desc = getSetterInChain(target, prop);
        if (desc?.set) {
          // desc.set.call(proxyCache.get(target) ?? target, value);
          desc.set.call(receiver, value);
        } else {
          const old = Reflect.get(target, prop, proxy);
          if (Object.is(old, value)) return true;
          proxyFor(old)?.[META]?.watchers.delete(bubbleCb);
          Reflect.set(target, prop, value);
          scheduleNotify(proxy);
        }
        return true;
      },

      deleteProperty(target, prop) {
        if (Object.hasOwn(target, prop)) {
          const old = Reflect.get(target, prop, proxy);
          proxyFor(old)?.[META]?.watchers.delete(bubbleCb);
          Reflect.deleteProperty(target, prop);
          scheduleNotify(proxy);
        }
        return true;
      },
    }) as ReactiveState<T>;

    proxyCache.set(obj, proxy);
    return proxy;
  }

  return deepProxy(initial) as ProxiedReactiveState<T>;
}

/**
 * Erlaubt auf einem ReactiveState auf Änderungen zu "listenen".
 * Wenn man "replacements" beobachten will, muss man einen selector angeben.
 * Bsp:
 * watch(store, cb, (s) => s.person.name)  — ruft cb auch auf, wenn die person instance ersetzt wird (proxy change)
 *
 * @param target - reactive state.
 * @param cb - change listener get's the state / selected state as argument.
 * @param selector - optional selector function to select a part of the state. This is rerun on every change in the target.
 * @returns unsubscriber
 */
export function watch<T extends AnyReactiveState>(target: T, cb: (value: T) => void): () => void;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function watch<T extends AnyReactiveState, S extends (value: T) => any>(
  target: T,
  cb: (value: ReturnType<S>) => void,
  selector: S,
): () => void;
export function watch<
  T extends AnyReactiveState,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  S extends ((value: T) => any) | undefined = undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
>(target: T, cb: (value: any) => void, selector?: S): () => void {
  if (!isReactiveState(target)) {
    throw new Error('watch requires a ReactiveState (created with createReactiveStore)');
  }
  const meta = getMeta(target);
  const watcher = selector ? () => cb(selector(target)) : () => cb(target);

  meta.watchers.add(watcher);
  return () => {
    meta.watchers.delete(watcher);
  };
}

export const addListener = watch;

export function unwrapProxy(value: undefined): undefined;
export function unwrapProxy<T extends object>(value: ReactiveState<T> | T): T;

export function unwrapProxy(value: object | undefined) {
  if (value === undefined) return undefined;
  const seen = new WeakMap<object, object>();

  function deep(v: unknown): unknown {
    if (!isObject(v)) return v;
    if (!canProxy(v)) return v;
    if (seen.has(v)) return seen.get(v);

    if (Array.isArray(v)) {
      const arr: unknown[] = [];
      seen.set(v, arr);
      for (let i = 0; i < v.length; i++) {
        arr[i] = deep(v[i]);
      }
      return arr;
    }

    const proto = Object.getPrototypeOf(v) as object | null;
    const plain = Object.create(proto ?? Object.prototype) as Record<string, unknown>;
    seen.set(v, plain);
    for (const key of Object.keys(v)) {
      plain[key] = deep((v as Record<string, unknown>)[key]);
    }
    return plain;
  }

  return deep(value);
}

// TODO maybe we need also: unwrapToJSON or similar
